import React from 'react';
import { Box, Typography, Grid, Chip, Paper, Divider } from '@mui/material';
import {
  BusinessCenter,
  AttachMoney,
  CalendarToday,
  Assignment,
  Person,
} from '@mui/icons-material';
import { formatDate, formatMoney } from 'lib/functions/utils';
import { SGR_TYPES, SGR_STATES } from 'lib/consts';
import PaymentDocument from './PaymentDocument';
import RiskPremiumCalculator from './RiskPremiumCalculator';

interface WarrantySummaryProps {
  warranty: any;
}

const WarrantySummary: React.FC<WarrantySummaryProps> = ({ warranty }) => {
  if (!warranty) return null;
  const warrantyType = SGR_TYPES.find(
    (type) => type.value === warranty.type,
  )?.label;
  const warrantyStatus = SGR_STATES.find(
    (state) => state.value === warranty.status,
  );

  const showDocument =
    warranty?.status === 'APPROVED' || warranty?.status === 'ACTIVE';

  const showCalculator =
    warranty?.status === 'QUOTED' || warranty?.status === 'IN_REVIEW';

  const daysDifference = Math.ceil(
    (new Date(warranty.endDate).getTime() -
      new Date(warranty.startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1rem',
                  lineHeight: 1.1,
                }}
              >
                Información General
              </Typography>
              <Chip
                label="Detalles"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
              <Chip
                label={warrantyStatus?.label}
                color={warrantyStatus?.color as any}
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BusinessCenter
                    sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Tipo de Garantía
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {warrantyType}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AttachMoney
                    sx={{ fontSize: '0.8rem', color: 'success.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Monto Cotizado
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {formatMoney(warranty.quotedAmount)}{' '}
                    {warranty.quotedCurrency}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Assignment
                    sx={{ fontSize: '0.8rem', color: 'warning.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Número de Licitación
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    #{warranty.tenderId}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Person
                    sx={{ fontSize: '0.8rem', color: 'secondary.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    RUT Mandante
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {warranty.principalRut}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper
            sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1rem',
                  lineHeight: 1.1,
                }}
              >
                Fechas Importantes
              </Typography>
              <Chip
                label="Cronología"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ height: '18px', fontSize: '0.65rem' }}
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarToday
                    sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Fecha de Cotización
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {formatDate(new Date(warranty.quoteDate), 'DD/MM/YYYY')}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarToday
                    sx={{ fontSize: '0.8rem', color: 'success.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Fecha de Inicio
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {formatDate(new Date(warranty.startDate), 'DD/MM/YYYY')}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CalendarToday
                    sx={{ fontSize: '0.8rem', color: 'error.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Fecha de Término
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {formatDate(new Date(warranty.endDate), 'DD/MM/YYYY')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {showDocument && (
        <Box sx={{ mt: 3 }}>
          <PaymentDocument document={warranty.documents[0]} />
        </Box>
      )}

      {showCalculator && (
        <Box sx={{ mt: 3 }}>
          <RiskPremiumCalculator
            amount={warranty.quotedAmount}
            days={daysDifference}
            conversionRate={warranty.conversionRate || 1}
            type={warranty.type}
            quotedCurrency={warranty.quotedCurrency}
          />
        </Box>
      )}
    </Box>
  );
};

export default WarrantySummary;
